import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { locationStore, requestCurrentLocation } from "@/lib/location";

type Status = "idle" | "requesting" | "granted" | "denied" | "unsupported";

const DEFAULT_MESSAGE = "开启定位后，将为你优先推荐附近可服务技师，并展示距离信息。";
const DENIED_HELP_TEXT = "如果系统没有再次弹出授权框，可以先继续浏览页面，稍后再尝试开启定位。";

export default function LocationPermissionGate() {
  const location = useLocation();
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [visible, setVisible] = useState(false);
  const skipLocationGate =
    location.pathname === "/"
    || location.pathname === "/customer"
    || location.pathname.startsWith("/customer/")
    || location.pathname.startsWith("/login")
    || location.pathname === "/service"
    || location.pathname === "/support-chat";

  const askForLocation = async () => {
    setStatus("requesting");
    setMessage(DEFAULT_MESSAGE);

    try {
      await requestCurrentLocation();
      setStatus("granted");
      setVisible(false);
    } catch (error) {
      locationStore.clear();
      const geolocationError = asGeolocationError(error);
      const rawMessage = error instanceof Error ? error.message : "";
      const denied = geolocationError?.code === 1 || isPermissionDenied(rawMessage);
      const nextMessage =
        denied
          ? "当前没有位置权限，暂时无法按真实位置计算技师距离。"
          : geolocationError
            ? "定位失败，请检查系统定位权限后重试。"
          : error instanceof Error
            ? error.message
            : "定位失败，请稍后重试。";

      setStatus(nextMessage.includes("不支持") ? "unsupported" : "denied");
      setMessage(nextMessage);
      setVisible(true);
    }
  };

  const handlePrimaryAction = () => {
    if (status === "unsupported") {
      setVisible(false);
      return;
    }
    void askForLocation();
  };

  useEffect(() => {
    if (skipLocationGate) {
      setVisible(false);
      return;
    }

    const cached = locationStore.getFresh();
    if (cached) {
      setStatus("granted");
      setVisible(false);
      return;
    }

    setStatus("idle");
    setMessage(DEFAULT_MESSAGE);
    setVisible(true);
  }, [skipLocationGate]);

  if (!visible || status === "granted") {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center"
      style={{ background: "rgba(18, 18, 18, 0.45)" }}
    >
      <div
        className="w-full rounded-t-[28px] bg-white px-5 pb-6 pt-5"
        style={{ maxWidth: 480, boxShadow: "0 -12px 40px rgba(0,0,0,0.16)" }}
      >
        <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-[#E7E2DA]" />
        <div
          className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl"
          style={{ background: "linear-gradient(135deg, #FFF0E5 0%, #FFE0C2 100%)" }}
        >
          <i className="ri-map-pin-user-fill text-2xl text-[#FF6A00]" />
        </div>
        <h3 className="text-lg font-bold text-[#1A1A1A]">允许获取当前位置</h3>
        <p className="mt-2 text-sm leading-6 text-[#666666]">{message}</p>
        {status === "denied" ? (
          <div
            className="mt-4 rounded-2xl px-3 py-3 text-xs leading-5"
            style={{ background: "#F8F5F1", color: "#8B7D6B" }}
          >
            {DENIED_HELP_TEXT}
          </div>
        ) : null}
        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={() => setVisible(false)}
            className="flex-1 rounded-full border border-[#E8E1D7] py-3 text-sm font-semibold text-[#666666]"
          >
            稍后再说
          </button>
          <button
            type="button"
            onClick={handlePrimaryAction}
            disabled={status === "requesting"}
            className="flex-1 rounded-full py-3 text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #FF9348 0%, #FF6400 100%)" }}
          >
            {status === "requesting" ? "定位中..." : status === "unsupported" ? "我知道了" : "允许定位"}
          </button>
        </div>
      </div>
    </div>
  );
}

function asGeolocationError(error: unknown): { code: number } | null {
  if (typeof error === "object" && error !== null && "code" in error && typeof error.code === "number") {
    return { code: error.code };
  }
  return null;
}

function isPermissionDenied(message: string) {
  const lower = message.toLowerCase();
  return lower.includes("deny") || lower.includes("denied") || message.includes("拒绝") || lower.includes("auth");
}
